import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { ScrollArea } from "../../components/ui/scroll-area";
import { useToast } from "../../components/ui/use-toast";
import { supabase } from "@os/integrations/supabase/client";
import { Search, Link as LinkIcon, FileText, Send, Clipboard, Bot, Sparkles } from "lucide-react";
import { operator } from "@os/lib/operator";
import { markdownToHtml } from "../../../lib/markdownToHtml";

type Mode = "search" | "fetch";

type GoferResult = {
  title: string;
  link: string;
  snippet?: string;
};

const isProbablyUrl = (text: string) => {
  try {
    const hasProtocol = /^https?:\/\//i.test(text);
    const url = new URL(hasProtocol ? text : `https://${text}`);
    return !!url.hostname;
  } catch {
    return false;
  }
};

const normalizeUrl = (input: string): string => {
  if (!/^https?:\/\//i.test(input)) {
    return `https://${input}`;
  }
  return input;
};

const toResults = (raw: any): GoferResult[] => {
  if (!raw) return [];
  const results = raw.results || raw; // our function returns { results } or cached { results }
  if (!results) return [];
  // Try common shapes
  const organic = results.organic_results || results.items || results.results || [];
  if (Array.isArray(organic) && organic.length > 0) {
    return organic
      .map((r: any) => ({
        title: String(r.title || r.name || r.link || "Untitled"),
        link: String(r.link || r.url || r.source || ""),
        snippet: typeof r.snippet === "string" ? r.snippet : (r.description || r.text || "")
      }))
      .filter((r: GoferResult) => r.link);
  }
  // Fallback: flatten top-level links if present
  if (Array.isArray(results)) {
    return results
      .map((r: any) => ({
        title: String(r.title || r.name || r.link || "Untitled"),
        link: String(r.link || r.url || ""),
        snippet: typeof r.snippet === "string" ? r.snippet : (r.description || r.text || "")
      }))
      .filter((r: GoferResult) => r.link);
  }
  return [];
};

const MapleGofer: React.FC = () => {
  const { toast } = useToast();
  const [mode, setMode] = useState<Mode>("search");
  const [query, setQuery] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<GoferResult[]>([]);
  const [content, setContent] = useState<string>("");
  const [currentUrl, setCurrentUrl] = useState<string>("");

  // ADD: AI state
  const [aiProvider, setAiProvider] = useState<"openai" | "maplept" | "deepseek">("openai");
  const [aiModel, setAiModel] = useState<string>("gpt-4o");
  const [aiQuestion, setAiQuestion] = useState<string>("");
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiOutput, setAiOutput] = useState<string>("");

  // Helper: wait for MapleWord to signal it's ready (fallback to timeout)
  const waitForMapleWordReady = (timeoutMs = 1500) =>
    new Promise<void>((resolve) => {
      let resolved = false;
      const handler = () => {
        if (!resolved) {
          resolved = true;
          window.removeEventListener("mapleos-mapleword-ready", handler as EventListener);
          resolve();
        }
      };
      window.addEventListener("mapleos-mapleword-ready", handler as EventListener);
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          window.removeEventListener("mapleos-mapleword-ready", handler as EventListener);
          resolve();
        }
      }, timeoutMs);
    });

  // Event-based integration: allow external triggers to search or fetch
  useEffect(() => {
    const onGoferSearch = (e: Event) => {
      const { query: q } = (e as CustomEvent<{ query: string }>).detail || { query: "" };
      if (!q) return;
      setMode("search");
      setQuery(q);
      void handleSearch(q);
    };
    const onGoferFetch = (e: Event) => {
      const { url } = (e as CustomEvent<{ url: string }>).detail || { url: "" };
      if (!url) return;
      setMode("fetch");
      setQuery(url);
      void handleFetch(url);
    };
    window.addEventListener("mapleos-gofer-search", onGoferSearch as EventListener);
    window.addEventListener("mapleos-gofer-fetch", onGoferFetch as EventListener);
    return () => {
      window.removeEventListener("mapleos-gofer-search", onGoferSearch as EventListener);
      window.removeEventListener("mapleos-gofer-fetch", onGoferFetch as EventListener);
    };
  }, []);

  const handleSearch = useCallback(async (q?: string) => {
    const term = (q ?? query).trim();
    if (!term) return;
    setLoading(true);
    setResults([]);
    setContent("");
    setCurrentUrl("");
    try {
      const { data, error } = await supabase.functions.invoke("web-search", {
        body: { query: term }
      });
      if (error) {
        throw new Error(error.message || "Search failed");
      }
      const parsed = toResults(data);
      setResults(parsed);
      if (parsed.length === 0) {
        toast({ title: "No results", description: "Try a different query.", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Search failed", description: err.message || "Unknown error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [query, toast]);

  const handleFetch = useCallback(async (urlInput?: string) => {
    const raw = (urlInput ?? query).trim();
    if (!raw) return;
    const finalUrl = isProbablyUrl(raw) ? normalizeUrl(raw) : `https://www.google.com/search?q=${encodeURIComponent(raw)}`;
    setLoading(true);
    setResults([]);
    setContent("");
    setCurrentUrl(finalUrl);
    try {
      const { data, error } = await supabase.functions.invoke("scrape-url", {
        body: { url: finalUrl }
      });
      if (error) {
        throw new Error(error.message || "Fetch failed");
      }
      // scrape-url returns { content }
      const text = data?.content || data?.results?.content || "";
      setContent(String(text || ""));
      if (!text) {
        toast({ title: "No content", description: "The page returned no text.", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Fetch failed", description: err.message || "Unknown error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [query, toast]);

  const openResult = useCallback(async (res: GoferResult) => {
    setQuery(res.link);
    await handleFetch(res.link);
  }, [handleFetch]);

  const copyContent = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast({ title: "Copied", description: "Text copied to clipboard." });
    } catch {
      toast({ title: "Copy failed", description: "Could not copy to clipboard.", variant: "destructive" });
    }
  }, [content, toast]);

  const sendToAI = useCallback(() => {
    if (!content) return;
    window.dispatchEvent(new CustomEvent("mapleos-gofer-send", { detail: { text: content, url: currentUrl } }));
    toast({ title: "Sent to AI", description: "Gofer text sent to Desktop AI." });
  }, [content, currentUrl, toast]);

  const onSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "search") {
      void handleSearch();
    } else {
      void handleFetch();
    }
  }, [mode, handleSearch, handleFetch]);

  // ADD: Prompt builders
  const buildContextPrompt = (mode: "qa" | "summary" | "keypoints" | "outline", text: string, question?: string) => {
    const truncated = (text || "").slice(0, 15000);
    switch (mode) {
      case "summary":
        return `Summarize the following page in 6-10 sentences, clear and factual:\n\n${truncated}`;
      case "keypoints":
        return `Extract 8-12 key points as bullet items from the following page. Be concise:\n\n${truncated}`;
      case "outline":
        return `Create a detailed outline suitable for a 1,000–1,500 word article based on the following page. Use headings and subpoints:\n\n${truncated}`;
      case "qa":
      default:
        return `Use only the following page content as context. If information is missing, say so clearly.\n\nCONTEXT:\n${truncated}\n\nQUESTION:\n${question || ""}`;
    }
  };

  // ADD: AI call via Supabase edge functions
  const askAI = async (mode: "qa" | "summary" | "keypoints" | "outline") => {
    const hasContext = (content || "").trim().length > 0;
    if (!hasContext) {
      toast({ title: "No page content", description: "Fetch a page first (Open URL) or run a Search and open a result.", variant: "destructive" });
      return;
    }
    if (mode === "qa" && !aiQuestion.trim()) {
      toast({ title: "Enter a question", description: "Type your question for the AI.", variant: "destructive" });
      return;
    }
    setAiLoading(true);
    setAiOutput("");

    const prompt = buildContextPrompt(mode, content, aiQuestion);
    try {
      if (aiProvider === "openai") {
        const { data, error } = await supabase.functions.invoke("openai-proxy", {
          body: {
            endpoint: "chat/completions",
            body: {
              model: aiModel || "gpt-4o",
              messages: [
                { role: "system", content: "You are a helpful research assistant." },
                { role: "user", content: prompt }
              ],
              max_tokens: 1500
            }
          }
        });
        if (error) throw new Error(error.message || "OpenAI proxy error");
        const text = data?.choices?.[0]?.message?.content || "";
        setAiOutput(text);

      } else if (aiProvider === "maplept") {
        const { data, error } = await supabase.functions.invoke("maplept-proxy", {
          body: {
            endpoint: "chat/completions",
            body: {
              model: aiModel || "",
              messages: [
                { role: "system", content: "You are a helpful research assistant." },
                { role: "user", content: prompt }
              ],
              max_tokens: 1500
            }
          }
        });
        if (error) throw new Error(error.message || "MaplePT proxy error");
        const text = data?.choices?.[0]?.message?.content || "";
        setAiOutput(text);

      } else if (aiProvider === "deepseek") {
        const { data, error } = await supabase.functions.invoke("deepseek-proxy", {
          body: {
            model: aiModel || "deepseek-coder",
            messages: [
              { role: "system", content: "You are a helpful research assistant." },
              { role: "user", content: prompt }
            ],
            max_tokens: 1500
          }
        });
        if (error) throw new Error(error.message || "DeepSeek proxy error");
        const text = data?.choices?.[0]?.message?.content || "";
        setAiOutput(text);
      }
    } catch (err: any) {
      toast({ title: "AI request failed", description: err.message || "Unknown error", variant: "destructive" });
    } finally {
      setAiLoading(false);
    }
  };

  // REPLACED: Send AI output to MapleWord with Markdown → HTML conversion and readiness wait
  const sendToMapleWord = async () => {
    if (!aiOutput.trim()) {
      toast({ title: "No AI output", description: "Run an AI action first.", variant: "destructive" });
      return;
    }

    const bodyHtml = await markdownToHtml(aiOutput.trim());
    const html = `<h1>${mode === "search" ? "Research Notes" : "Writing Draft"}</h1>${bodyHtml}`;

    try {
      await operator.openWindow("maple-word", { title: "MapleWord" });
      await waitForMapleWordReady();
      window.dispatchEvent(new CustomEvent("mapleos-mapleword-insert", {
        detail: { html, title: "Research Draft" }
      }));
      toast({ title: "Sent to MapleWord", description: "Content inserted into MapleWord." });
    } catch (e: any) {
      // Fallback: dispatch even if openWindow failed (MapleWord might already be open)
      window.dispatchEvent(new CustomEvent("mapleos-mapleword-insert", {
        detail: { html, title: "Research Draft" }
      }));
      toast({ title: "Sent to MapleWord", description: "If MapleWord is open, content will appear." });
    }
  };

  return (
    <div className="h-full w-full flex flex-col bg-background">
      <div className="border-b border-border">
        <div className="flex items-center gap-2 p-2">
          <div className="flex gap-1 rounded-md border bg-muted/30 p-1">
            <Button
              size="sm"
              variant={mode === "search" ? "default" : "ghost"}
              onClick={() => setMode("search")}
            >
              <Search className="h-4 w-4 mr-1" /> Search
            </Button>
            <Button
              size="sm"
              variant={mode === "fetch" ? "default" : "ghost"}
              onClick={() => setMode("fetch")}
            >
              <LinkIcon className="h-4 w-4 mr-1" /> Open URL
            </Button>
          </div>
          <form onSubmit={onSubmit} className="flex-1 flex items-center gap-2">
            <div className="flex-1">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={mode === "search" ? "Search the web (text-only)..." : "Enter URL to fetch text..."}
                className="font-mono text-sm"
              />
            </div>
            <Button type="submit" size="sm" disabled={loading}>
              {mode === "search" ? <Search className="h-4 w-4 mr-1" /> : <LinkIcon className="h-4 w-4 mr-1" />}
              {loading ? "Working..." : (mode === "search" ? "Search" : "Fetch")}
            </Button>
          </form>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-3 p-3">
        <Card className="p-3 overflow-hidden flex flex-col">
          <div className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
            <FileText className="h-4 w-4" /> Results
          </div>
          <ScrollArea className="flex-1">
            {results.length === 0 && (
              <div className="text-sm text-muted-foreground p-2">
                {mode === "search"
                  ? "Enter a query and press Search. Click a result to fetch and display the page text."
                  : "Enter a URL and press Fetch to display text-only content."}
              </div>
            )}
            <div className="space-y-2">
              {results.map((r, idx) => (
                <button
                  key={`${r.link}-${idx}`}
                  onClick={() => openResult(r)}
                  className="w-full text-left rounded-md border hover:bg-accent hover:text-accent-foreground p-2 transition-colors"
                >
                  <div className="font-medium truncate">{r.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.link}</div>
                  {r.snippet && (
                    <div className="text-xs mt-1 line-clamp-2">{r.snippet}</div>
                  )}
                </button>
              ))}
            </div>
          </ScrollArea>
        </Card>

        <Card className="p-3 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" /> Text Content {currentUrl ? `— ${currentUrl}` : ""}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={copyContent} disabled={!content}>
                <Clipboard className="h-4 w-4 mr-1" /> Copy
              </Button>
              <Button variant="outline" size="sm" onClick={sendToAI} disabled={!content}>
                <Send className="h-4 w-4 mr-1" /> Send to AI
              </Button>
            </div>
          </div>
          <div className="flex-1 border rounded-md p-3 overflow-auto bg-background/60">
            {content ? (
              <pre className="whitespace-pre-wrap text-sm font-mono">{content}</pre>
            ) : (
              <div className="text-sm text-muted-foreground">No content yet.</div>
            )}
          </div>
        </Card>

        <Card className="p-3 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <Bot className="h-4 w-4" />
            <div className="text-xs text-muted-foreground">AI Assistant</div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <select
              className="border rounded px-2 py-1 text-sm bg-background"
              value={aiProvider}
              onChange={(e) => {
                const p = e.target.value as typeof aiProvider;
                setAiProvider(p);
                // simple defaults per provider
                if (p === "openai") setAiModel("gpt-4o");
                if (p === "maplept") setAiModel(""); // default model on proxy side
                if (p === "deepseek") setAiModel("deepseek-coder");
              }}
            >
              <option value="openai">OpenAI (GPT-4o)</option>
              <option value="maplept">MaplePT-mini</option>
              <option value="deepseek">DeepSeek Coder</option>
            </select>
            <input
              className="border rounded px-2 py-1 text-sm bg-background"
              value={aiModel}
              onChange={(e) => setAiModel(e.target.value)}
              placeholder={aiProvider === "openai" ? "gpt-4o" : aiProvider === "deepseek" ? "deepseek-coder" : "(default)"}
            />
          </div>

          <textarea
            className="border rounded p-2 text-sm bg-background min-h-[80px] mb-2"
            placeholder="Ask a question about the page…"
            value={aiQuestion}
            onChange={(e) => setAiQuestion(e.target.value)}
          />

          <div className="flex flex-wrap gap-2 mb-2">
            <Button size="sm" variant="default" disabled={aiLoading} onClick={() => askAI("qa")}>
              <Sparkles className="h-4 w-4 mr-1" /> Ask AI
            </Button>
            <Button size="sm" variant="outline" disabled={aiLoading} onClick={() => askAI("summary")}>
              Summary
            </Button>
            <Button size="sm" variant="outline" disabled={aiLoading} onClick={() => askAI("keypoints")}>
              Key points
            </Button>
            <Button size="sm" variant="outline" disabled={aiLoading} onClick={() => askAI("outline")}>
              Outline
            </Button>
          </div>

          <div className="flex-1 border rounded-md p-3 overflow-auto bg-background/60">
            {aiLoading ? (
              <div className="text-sm text-muted-foreground">Thinking…</div>
            ) : aiOutput ? (
              <pre className="whitespace-pre-wrap text-sm font-mono">{aiOutput}</pre>
            ) : (
              <div className="text-sm text-muted-foreground">Run an AI action to see results here.</div>
            )}
          </div>

          <div className="mt-2 flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                await navigator.clipboard.writeText(aiOutput);
                toast({ title: "Copied", description: "AI output copied to clipboard." });
              } catch {
                toast({ title: "Copy failed", description: "Could not copy.", variant: "destructive" });
              }
            }} disabled={!aiOutput}>
              <Clipboard className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button variant="default" size="sm" onClick={sendToMapleWord} disabled={!aiOutput}>
              <FileText className="h-4 w-4 mr-1" /> Send to MapleWord
            </Button>
          </div>
        </Card>
      </div>

      <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
        MapleGofer • Text-only browsing via web-search and scrape-url • FAST + EFFICIENT
      </div>
    </div>
  );
};

export default MapleGofer;